import React from "react";
import RapidCaseSelector from "./RapidCaseList";
import RapidCaseScreen from "./RapidCaseScreen";
import RapidGradeScreen from "./RapidGradeScreen";
import RapidNewGrading from "./RapidNewGrading";
import { PreviousAttemptsModal, SettingsModal } from "./RapidCcsModals";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight, faList } from "@fortawesome/free-solid-svg-icons";
import { faHouseReturn } from "@fortawesome/pro-solid-svg-icons";
import { faArrowAltSquareRight } from "@fortawesome/pro-regular-svg-icons";
import GamificationToastWrapper from "../Gamification/GamificationToastWrapper";

const BASE = "https://app.ccscases.com";

function request(
  getToken,
  path,
  { method = "POST", body, signal } = {},
  onUnauthorized,
) {
  const headers = { TOKEN: getToken() || "" };
  const opts = { method, headers, signal };

  if (body !== undefined) {
    headers["Content-Type"] = "application/json";
    opts.body = JSON.stringify(body);
  }

  return fetch(BASE + path, opts).then(function (res) {
    if (!res.ok) {
      if (res.status === 401 && onUnauthorized) {
        // Same signal the rest of the app acts on: bounce back to login
        // instead of leaving the user staring at a dead screen.
        onUnauthorized();
      }
      const err = new Error("Request failed: " + res.status + " " + path);
      err.status = res.status;
      throw err;
    }
    // Some endpoints may return empty bodies; guard JSON parse.
    return res.text().then(function (text) {
      if (!text) return null;
      try {
        return JSON.parse(text);
      } catch (e) {
        return text;
      }
    });
  });
}

// ---------------------------------------------------------------------------
// Autocomplete helper: debounce + cancel in-flight + min length + session cache
// ---------------------------------------------------------------------------
// Usage (inside the case screen / modals, via props.api.createOrderSearch):
//   this.search = this.props.api.createOrderSearch();
//   this.search(term, (results) => this.setState({ results }), (err) => {...});
//   // call this.search.cancel() in componentWillUnmount
function makeCreateOrderSearch(getRapidOrders) {
  return function createOrderSearch(options) {
    const opts = options || {};
    const minLength = opts.minLength || 3;
    const debounceMs = opts.debounceMs || 250;
    const maxResults = opts.maxResults || 15;

    const cache = new Map(); // key: lowercased term -> results array
    let timer = null;
    let controller = null;

    function run(rawTerm, onResults, onError) {
      const term = (rawTerm || "").trim();

      // Clear pending debounce.
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }

      // Below threshold: clear results, cancel any in-flight request.
      if (term.length < minLength) {
        if (controller) {
          controller.abort();
          controller = null;
        }
        onResults([]);
        return;
      }

      const key = term.toLowerCase();

      // Session cache hit — return immediately, no network.
      if (cache.has(key)) {
        onResults(cache.get(key));
        return;
      }

      timer = setTimeout(function () {
        // Cancel the previous in-flight request.
        if (controller) controller.abort();
        controller = new AbortController();
        const localController = controller;

        getRapidOrders(term, localController.signal)
          .then(function (results) {
            const list = Array.isArray(results)
              ? results.slice(0, maxResults)
              : [];
            cache.set(key, list);
            // Ignore if a newer request superseded this one.
            if (localController === controller) {
              onResults(list);
            }
          })
          .catch(function (err) {
            if (err && err.name === "AbortError") return; // superseded — ignore
            if (onError) onError(err);
          });
      }, debounceMs);
    }

    run.cancel = function () {
      if (timer) clearTimeout(timer);
      if (controller) controller.abort();
      timer = null;
      controller = null;
    };

    run.clearCache = function () {
      cache.clear();
    };

    return run;
  };
}

// ---------------------------------------------------------------------------
// Physical exam metadata: backend token <-> display name + source field on the
// startrapidcase PhysicalExam object. Passed to the case screen as a prop.
// ---------------------------------------------------------------------------
const PHYSICAL_EXAM_COMPONENTS = [
  {
    token: "PhysicalExam-general",
    display: "Physical Exam: General Appearance",
    field: "GENERALAPPREARANCE",
  },
  { token: "PhysicalExam-Skin", display: "Physical Exam: Skin", field: "SKIN" },
  {
    token: "PhysicalExam-Breast",
    display: "Physical Exam: Breasts",
    field: "BREAST",
  },
  {
    token: "PhysicalExam-Lymphnodes",
    display: "Physical Exam: Lymph Nodes",
    field: "LYMPHNODE",
  },
  {
    token: "PhysicalExam-heenNeck",
    display: "Physical Exam: HEENT / Neck",
    field: "HEENTNECK",
  },
  {
    token: "PhysicalExam-Chest",
    display: "Physical Exam: Chest / Lungs",
    field: "CHESTLUNG",
  },
  {
    token: "PhysicalExam-Cardio",
    display: "Physical Exam: Heart / Cardiovascular",
    field: "CARDIOVASCULAR",
  },
  {
    token: "PhysicalExam-Abdomen",
    display: "Physical Exam: Abdomen",
    field: "ABDOMINAL",
  },
  {
    token: "PhysicalExam-genitalia",
    display: "Physical Exam: Genitalia",
    field: "GENITAL",
  },
  {
    token: "PhysicalExam-Rectal",
    display: "Physical Exam: Rectal",
    field: "RECTAL",
  },
  {
    token: "PhysicalExam-extremitiesSpine",
    display: "Physical Exam: Extremities / Spine",
    field: "EXTREMITIESSPINE",
  },
  {
    token: "PhysicalExam-Neuro",
    display: "Physical Exam: Neuro / Psych",
    field: "NEUROLOGICPSYCHOLOGIC",
  },
];

// ---------------------------------------------------------------------------
// API factory - Old style hook
// ---------------------------------------------------------------------------
function createApi(getToken, onUnauthorized) {
  const req = function (path, opts) {
    return request(getToken, path, opts, onUnauthorized);
  };

  // GET /listrapidcases.webapi
  function listRapidCases(signal) {
    return req("/listrapidcases.webapi", { method: "GET", signal });
  }

  // POST /startrapidcase.webapi  { CaseId }
  function startCaseRapid(caseId, signal) {
    return req("/startrapidcase.webapi", {
      body: { CaseId: caseId },
      signal,
    });
  }

  // POST /saverapidmarkedcases.webapi  { CaseId, Marked }
  function saveRapidMarkedCase(caseId, marked, signal) {
    return req("/saverapidmarkedcases.webapi", {
      body: { CaseId: caseId, Marked: marked },
      signal,
    });
  }

  // POST /saverapidccssettings.webapi
  // { AutoPhysicalExam, PreventiveCareGrading, HideRapidDisclaimer, ClassicGradingPage,
  //   ShowDiagnosis, HideCaseTitle, HideCategory, HideCompleted, HideIncomplete, ReviewLaterOnly }
  function saveRapidCcsSettings(settings, signal) {
    return req("/saverapidccssettings.webapi", {
      body: {
        AutoPhysicalExam: !!settings.AutoPhysicalExam,
        PreventiveCareGrading: !!settings.PreventiveCareGrading,
        HideRapidDisclaimer: !!settings.HideRapidDisclaimer,
        ClassicGradingPage: !!settings.ClassicGradingPage,
        // Case-list display/filter settings
        ShowDiagnosis: !!settings.ShowDiagnosis,
        HideCaseTitle: !!settings.HideCaseTitle,
        HideCategory: !!settings.HideCategory,
        HideCompleted: !!settings.HideCompleted,
        HideIncomplete: !!settings.HideIncomplete,
        ReviewLaterOnly: !!settings.ReviewLaterOnly,
      },
      signal,
    });
  }

  // POST /getrapidorderlist.webapi   (returns saved rapid order lists)
  function getRapidOrderLists(signal) {
    return req("/getrapidorderlist.webapi", { body: {}, signal });
  }

  // POST /saverapidorderlist.webapi  { Name, OrderIds }
  function saveRapidOrderList(name, orderIds, signal) {
    return req("/saverapidorderlist.webapi", {
      body: { Name: name, OrderIds: orderIds },
      signal,
    });
  }

  // POST /deleterapidorderlist.webapi  { Name }
  function deleteRapidOrderList(name, signal) {
    return req("/deleterapidorderlist.webapi", {
      body: { Name: name },
      signal,
    });
  }

  // POST /getrapidorders.webapi  { OrderName }  (autocomplete)
  function getRapidOrders(orderName, signal) {
    return req("/getrapidorders.webapi", {
      body: { OrderName: orderName },
      signal,
    });
  }

  // POST /getrapidresults.webapi  { CaseId, CurrentState, OrderIds }
  function getResultsRapid(caseId, currentState, orderIds, signal) {
    return req("/getrapidresults.webapi", {
      body: { CaseId: caseId, CurrentState: currentState, OrderIds: orderIds },
      signal,
    });
  }

  // POST /progressrapidccs.webapi  { CaseId, CurrentState, OrderSequence }
  function progressRapidCcs(caseId, currentState, orderSequence, signal) {
    return req("/progressrapidccs.webapi", {
      body: {
        CaseId: caseId,
        CurrentState: currentState,
        OrderSequence: orderSequence,
      },
      signal,
    });
  }

  // POST /rapidccsgrading.webapi
  // { CaseId, AutoPhysicalExam, PreventiveCareGrading, OrderSequence }
  function gradeRapidCcs(input, signal) {
    return req("/rapidccsgrading.webapi", {
      body: {
        CaseId: input.CaseId,
        AutoPhysicalExam: !!input.AutoPhysicalExam,
        PreventiveCareGrading: !!input.PreventiveCareGrading,
        OrderSequence: input.OrderSequence,
      },
      signal,
    });
  }

  // POST /rapidccsgetgrade.webapi  { CaseId, CaseAttemptId }
  // Review mode: instead of grading a new OrderSequence, passing the ID of a
  // previous attempt (from listrapidcases -> caseRow.Attempts[n].ID) returns
  // the stored grade for that attempt. Same response shape as gradeRapidCcs,
  // so the result feeds straight into RapidGradeScreen / RapidNewGrading.
  // Uses its own endpoint (rather than rapidccsgrading.webapi) since this is
  // fetching a previously-graded attempt individually, not grading a
  // just-finished exam.
  function getGradeForAttempt(caseId, attemptId, signal) {
    return req("/rapidccsgetgrade.webapi", {
      body: { CaseId: caseId, CaseAttemptId: attemptId },
      signal,
    });
  }

  // POST /feedback.webapi  { feedback, caseId?, caseDiagnosis? }
  function submitFeedback(feedbackText, caseId, caseDiagnosis, signal) {
    return req("/feedback.webapi", {
      body: {
        feedback: "RAPIDCCS FEEDBACK: " + feedbackText,
        ...(caseId !== undefined && caseId !== null && { caseId }),
        ...(caseDiagnosis !== undefined &&
          caseDiagnosis !== null && { caseDiagnosis }),
      },
      signal,
    });
  }

  return {
    listRapidCases: listRapidCases,
    startCaseRapid: startCaseRapid,
    saveRapidMarkedCase: saveRapidMarkedCase,
    saveRapidCcsSettings: saveRapidCcsSettings,
    getRapidOrderLists: getRapidOrderLists,
    saveRapidOrderList: saveRapidOrderList,
    deleteRapidOrderList: deleteRapidOrderList,
    getRapidOrders: getRapidOrders,
    getResultsRapid: getResultsRapid,
    progressRapidCcs: progressRapidCcs,
    gradeRapidCcs: gradeRapidCcs,
    getGradeForAttempt: getGradeForAttempt,
    submitFeedback: submitFeedback,
    createOrderSearch: makeCreateOrderSearch(getRapidOrders),
    PHYSICAL_EXAM_COMPONENTS: PHYSICAL_EXAM_COMPONENTS,
  };
}

// ===========================================================================
// DISCLAIMER MODAL
// ===========================================================================
// Shown once when the user first enters Rapid CCS (unless the
// HideRapidDisclaimer setting is on). "Continue" requires the acknowledgement
// checkbox; "Cancel" backs the user out of Rapid CCS entirely via onCancel.

class RapidCcsDisclaimerModal extends React.Component {
  state = {
    acknowledged: false,
    hover: null, // "cancel" | "continue" | null
  };

  toggleAcknowledged = () => {
    this.setState(function (prevState) {
      return { acknowledged: !prevState.acknowledged };
    });
  };

  handleContinue = () => {
    if (!this.state.acknowledged) return;
    this.props.onContinue();
  };

  render() {
    const { acknowledged, hover } = this.state;

    const overlayStyle = {
      position: "fixed",
      inset: 0,
      background: "rgba(20, 15, 15, 0.55)",
      backdropFilter: "blur(2px)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 1000,
    };

    const dialogStyle = {
      width: "100%",
      maxWidth: 480,
      background: "#ffffff",
      borderRadius: 14,
      boxShadow: "0 20px 60px rgba(0,0,0,0.35)",
      overflow: "hidden",
      borderTop: "5px solid oklch(0.55 0.2 25)",
      fontFamily: "'Inter', system-ui, sans-serif",
      margin: 16,
    };

    const cancelStyle = {
      background: hover === "cancel" ? "oklch(0.97 0.005 250)" : "#fff",
      color: "oklch(0.35 0.01 250)",
      border: "1px solid oklch(0.85 0.005 250)",
      padding: "11px 20px",
      borderRadius: 8,
      fontSize: 14,
      fontWeight: 600,
      cursor: "pointer",
      fontFamily: "inherit",
    };

    const continueStyle = acknowledged
      ? {
          background:
            hover === "continue"
              ? "oklch(0.44 0.16 255)"
              : "oklch(0.5 0.16 255)",
          color: "#fff",
          border: "none",
          padding: "11px 20px",
          borderRadius: 8,
          fontSize: 14,
          fontWeight: 600,
          cursor: "pointer",
          fontFamily: "inherit",
        }
      : {
          background: "oklch(0.9 0.01 255)",
          color: "oklch(0.6 0.01 250)",
          border: "none",
          padding: "11px 20px",
          borderRadius: 8,
          fontSize: 14,
          fontWeight: 600,
          cursor: "not-allowed",
          fontFamily: "inherit",
        };

    return (
      <div style={overlayStyle}>
        <div role="alertdialog" aria-modal="true" style={dialogStyle}>
          <div
            style={{
              padding: "28px 28px 20px",
              display: "flex",
              gap: 16,
              alignItems: "flex-start",
            }}
          >
            <div
              style={{
                flexShrink: 0,
                width: 42,
                height: 42,
                borderRadius: "50%",
                background: "oklch(0.95 0.04 25)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                <path
                  d="M12 2L1 21h22L12 2z"
                  stroke="oklch(0.55 0.2 25)"
                  strokeWidth="2"
                  strokeLinejoin="round"
                  fill="oklch(0.95 0.04 25)"
                />
                <path
                  d="M12 9v5"
                  stroke="oklch(0.55 0.2 25)"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
                <circle cx="12" cy="17" r="1" fill="oklch(0.55 0.2 25)" />
              </svg>
            </div>
            <div>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: "oklch(0.55 0.2 25)",
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  marginBottom: 4,
                }}
              >
                Before You Continue
              </div>
              <div
                style={{
                  fontSize: 19,
                  fontWeight: 800,
                  color: "oklch(0.2 0.01 250)",
                  lineHeight: 1.3,
                }}
              >
                WARNING! Rapid CCS is NOT how the USMLE functions!
              </div>
            </div>
          </div>

          <div
            style={{
              padding: "0 28px 8px",
              fontSize: 14.5,
              lineHeight: 1.65,
              color: "oklch(0.35 0.01 250)",
            }}
          >
            <p style={{ margin: "0 0 12px" }}>
              This mode is designed for users who are already very familiar with
              how USMLE CCS cases function —{" "}
              <strong>
                how to order, confirm orders, and advance the clock
              </strong>
              .
            </p>
            <p style={{ margin: 0 }}>
              Rapid CCS simplifies that process so you can move through cases
              faster than the official interface allows. It's built for studying
              when you don't have time to complete each case the standard way,
              but still want to reinforce the underlying process.
            </p>
          </div>

          <div
            style={{
              margin: "20px 28px 0",
              padding: "14px 16px",
              background: "oklch(0.97 0.01 60)",
              border: "1px solid oklch(0.88 0.03 60)",
              borderRadius: 8,
            }}
          >
            <label
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: 10,
                cursor: "pointer",
              }}
            >
              <input
                type="checkbox"
                checked={acknowledged}
                onChange={this.toggleAcknowledged}
                style={{
                  marginTop: 2,
                  width: 17,
                  height: 17,
                  accentColor: "oklch(0.5 0.16 255)",
                  cursor: "pointer",
                  flexShrink: 0,
                }}
              />
              <span
                style={{
                  fontSize: 13.5,
                  lineHeight: 1.5,
                  color: "oklch(0.3 0.01 250)",
                  fontWeight: 500,
                }}
              >
                I understand that Rapid CCS is not how the USMLE CCS Cases will
                function on test day. This is for studying purposes only.
              </span>
            </label>
          </div>

          <div
            style={{
              display: "flex",
              gap: 10,
              padding: "24px 28px 28px",
              justifyContent: "flex-end",
            }}
          >
            <button
              onClick={this.props.onCancel}
              onMouseEnter={() => this.setState({ hover: "cancel" })}
              onMouseLeave={() => this.setState({ hover: null })}
              style={cancelStyle}
            >
              Cancel
            </button>
            <button
              onClick={this.handleContinue}
              disabled={!acknowledged}
              onMouseEnter={() => this.setState({ hover: "continue" })}
              onMouseLeave={() => this.setState({ hover: null })}
              style={continueStyle}
            >
              Continue to Rapid CCS
            </button>
          </div>
        </div>
      </div>
    );
  }
}

// ===========================================================================
// APP
// ===========================================================================

const DEFAULT_SETTINGS = {
  AutoPhysicalExam: true,
  PreventiveCareGrading: false,
  HideRapidDisclaimer: false,
  // Classic (RapidNewGrading) is now the default grade viewer.
  ClassicGradingPage: true,

  // Case-list display/filter settings — all off by default, same as the
  // selector's previous local-state defaults.
  ShowDiagnosis: false,
  HideCaseTitle: false,
  HideCategory: false,
  HideCompleted: false,
  HideIncomplete: false,
  ReviewLaterOnly: false,
};

function parseInitialSettings(raw, props) {
  let parsed = raw;
  if (typeof raw === "string") {
    if (!raw.trim()) return Object.assign({}, DEFAULT_SETTINGS);
    try {
      parsed = JSON.parse(raw);
    } catch (e) {
      return Object.assign({}, DEFAULT_SETTINGS);
    }
  }
  if (!parsed || typeof parsed !== "object") {
    return Object.assign({}, DEFAULT_SETTINGS);
  }

  return {
    AutoPhysicalExam:
      parsed.AutoPhysicalExam !== undefined
        ? !!parsed.AutoPhysicalExam
        : DEFAULT_SETTINGS.AutoPhysicalExam,
    PreventiveCareGrading:
      parsed.PreventiveCareGrading !== undefined
        ? !!parsed.PreventiveCareGrading
        : DEFAULT_SETTINGS.PreventiveCareGrading,
    HideRapidDisclaimer:
      props.rapidCCSDismissed === true
        ? props.rapidCCSDismissed
        : parsed.HideRapidDisclaimer !== undefined
          ? !!parsed.HideRapidDisclaimer
          : DEFAULT_SETTINGS.HideRapidDisclaimer,
    ClassicGradingPage:
      parsed.ClassicGradingPage !== undefined
        ? !!parsed.ClassicGradingPage
        : DEFAULT_SETTINGS.ClassicGradingPage,

    // Case-list display/filter settings
    ShowDiagnosis:
      parsed.ShowDiagnosis !== undefined
        ? !!parsed.ShowDiagnosis
        : DEFAULT_SETTINGS.ShowDiagnosis,
    HideCaseTitle:
      parsed.HideCaseTitle !== undefined
        ? !!parsed.HideCaseTitle
        : DEFAULT_SETTINGS.HideCaseTitle,
    HideCategory:
      parsed.HideCategory !== undefined
        ? !!parsed.HideCategory
        : DEFAULT_SETTINGS.HideCategory,
    HideCompleted:
      parsed.HideCompleted !== undefined
        ? !!parsed.HideCompleted
        : DEFAULT_SETTINGS.HideCompleted,
    HideIncomplete:
      parsed.HideIncomplete !== undefined
        ? !!parsed.HideIncomplete
        : DEFAULT_SETTINGS.HideIncomplete,
    ReviewLaterOnly:
      parsed.ReviewLaterOnly !== undefined
        ? !!parsed.ReviewLaterOnly
        : DEFAULT_SETTINGS.ReviewLaterOnly,
  };
}

class RapidCcsApp extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      // Data owned here and passed down as props.
      cases: [],
      marked: [], // array of CaseIds
      loading: true,
      error: null,

      // Screen switching: "selector" | "case" | "grade"
      screen: "selector",
      activeCase: null,

      // Bumped by retryCase so the case screen remounts (key change) and
      // startCaseRapid runs again for a fresh attempt.
      caseAttempt: 0,

      // Grade result for the grade screen.
      grade: null,
      gradedDiagnosis: null,

      // Previous-attempts modal. Set to a case row when the user clicks a
      // case that already has attempts; null when closed. While a stored
      // grade is being fetched, attemptLoadingId holds that attempt's ID.
      attemptsCase: null,
      attemptLoadingId: null,
      attemptError: null,

      // Settings modal. Owned here (rather than in RapidCaseScreen) so it can
      // be opened from both the case selector and the case screen.
      showSettings: false,

      // Which grade view is showing: "rapid" (RapidGradeScreen) or
      // "classic" (RapidNewGrading — the regular app's NewGrading look).
      // Seeded from the persisted ClassicGradingPage setting (Classic is the
      // default). Toggled from the app bar while on the grade screen; the
      // choice is persisted back to RapidCcsSettings and so applies to
      // future sessions too.
      gradeView: parseInitialSettings(props.initialSettings, props)
        .ClassicGradingPage
        ? "classic"
        : "rapid",

      // Settings the case screen needs. Seeded from the login response's
      // RapidCcsSettings field (passed in as the initialSettings prop —
      // JSON string or object, see parseInitialSettings). Persisted by the
      // SettingsModal itself (via api.saveRapidCcsSettings) — updateSettings
      // only stores the applied result here.
      settings: parseInitialSettings(props.initialSettings, props),

      // The entry disclaimer. Starts unseen; once the user continues past it
      // (or the HideRapidDisclaimer setting is on) it stays dismissed for the
      // rest of the session.
      disclaimerDismissed: false,

      // TODO: fontScale — SettingsModal already accepts optional
      // fontScale/onFontScaleChange/fontSteps props; to reintroduce, hold
      // fontScale here, thread the props through RapidCaseScreen, and feed
      // it into --rccs-font-scale below (fixed at 1 for now).
    };

    // The API layer reads the token from the prop at request time — this is
    // the only place the token is ever sourced from. If the host updates the
    // token prop, subsequent requests pick it up automatically.
    //
    // Every endpoint shares one onUnauthorized callback so a 401 on *any*
    // Rapid CCS request (case list, order search, grading, etc.) surfaces
    // the same unauthenticated flow the rest of the app uses, instead of
    // leaving the user stuck on a broken screen inside this overlay.
    this.api = createApi(
      () => this.props.token,
      () => {
        if (this.props.setAuthenticationError) {
          this.props.setAuthenticationError();
        }
      },
    );

    this._mounted = false;
    this._controller = null;

    // Last scroll position of the case list, kept across screen switches so
    // returning to the list lands where the user left it. Instance field
    // (not state) so scrolling never triggers a re-render.
    this.caseListScrollTop = 0;
  }

  componentDidMount() {
    this.handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = "";
      return "";
    };
    window.addEventListener("beforeunload", this.handleBeforeUnload);

    this._mounted = true;
    this.fetchCases();

    this.updateViewportHeight = () => {
      if (!this._mounted) return;
      const vv = typeof window !== "undefined" ? window.visualViewport : null;
      const h = vv ? vv.height : window.innerHeight;
      this.setState((prev) =>
        prev.viewportHeight === h ? null : { viewportHeight: h },
      );
    };
    this.updateViewportHeight();
    if (typeof window !== "undefined") {
      if (window.visualViewport) {
        window.visualViewport.addEventListener(
          "resize",
          this.updateViewportHeight,
        );
        window.visualViewport.addEventListener(
          "scroll",
          this.updateViewportHeight,
        );
      } else {
        window.addEventListener("resize", this.updateViewportHeight);
      }
      window.addEventListener("orientationchange", this.updateViewportHeight);
    }

    this.handleFocusIn = (e) => {
      const el = e.target;
      if (!el || (el.tagName !== "INPUT" && el.tagName !== "TEXTAREA")) return;
      setTimeout(() => {
        if (el === document.activeElement) {
          el.scrollIntoView({ block: "center", behavior: "smooth" });
        }
      }, 300);
    };
    if (typeof document !== "undefined") {
      document.addEventListener("focusin", this.handleFocusIn);
    }
  }

  componentWillUnmount() {
    window.removeEventListener("beforeunload", this.handleBeforeUnload);

    this._mounted = false;
    if (this._controller) this._controller.abort();
    if (this._attemptController) this._attemptController.abort();

    if (typeof window !== "undefined") {
      if (window.visualViewport) {
        window.visualViewport.removeEventListener(
          "resize",
          this.updateViewportHeight,
        );
        window.visualViewport.removeEventListener(
          "scroll",
          this.updateViewportHeight,
        );
      } else {
        window.removeEventListener("resize", this.updateViewportHeight);
      }
      window.removeEventListener(
        "orientationchange",
        this.updateViewportHeight,
      );
    }
    if (typeof document !== "undefined") {
      document.removeEventListener("focusin", this.handleFocusIn);
    }
  }

  applySettings = (nextSettings, extraState) => {
    if (this.props.onSettingsChange) {
      this.props.onSettingsChange(nextSettings);
    }
    this.setState(Object.assign({ settings: nextSettings }, extraState));
  };

  setCaseListFilter = (key, value) => {
    const nextSettings = Object.assign({}, this.state.settings, {
      [key]: !!value,
    });
    this.applySettings(nextSettings);
  };

  // Called by the case list on every scroll; read back as initialScrollTop
  // when the list remounts.
  saveCaseListScroll = (top) => {
    this.caseListScrollTop = top;
  };

  // ── Persisting settings ───────────────────────────────────────────────────
  // Settings only hit the network at a handful of deliberate moments —
  // starting a new case, reviewing a case, or navigating back to CCS Cases /
  // the Rapid Case List — instead of on every toggle/checkbox click. Reads
  // this.state.settings at call time, so it always sends whatever is
  // currently applied (filters, grade view, disclaimer flag, etc.).
  persistSettings = () => {
    this.api.saveRapidCcsSettings(this.state.settings).catch(function () {});
  };

  // ── Data fetching / mutations (passed down as props) ─────────────────────

  fetchCases = () => {
    if (this._controller) this._controller.abort();
    this._controller = new AbortController();
    this.setState({ loading: true, error: null });

    this.api
      .listRapidCases(this._controller.signal)
      .then((data) => {
        if (!this._mounted) return;
        // Tolerate either a bare array of cases, or an object wrapper that
        // also carries the marked-case IDs (e.g. { Cases:[...], MarkedCases:[...] }).
        const list = Array.isArray(data)
          ? data
          : (data && (data.Cases || data.RapidCases)) || [];

        // Marked cases can arrive three ways; support all so stars show on
        // load once the backend includes them:
        //  1) a top-level array: MarkedCases / SavedRapidMarkedCases
        //  2) per-row flags: MARKED / ReviewLater
        let markedIds = null;
        if (data && Array.isArray(data.MarkedCases)) {
          markedIds = data.MarkedCases;
        } else if (data && Array.isArray(data.SavedRapidMarkedCases)) {
          markedIds = data.SavedRapidMarkedCases;
        } else {
          const fromRows = list
            .filter(function (c) {
              return (
                c.MARKED === true ||
                c.ReviewLater === true ||
                c.MarkedForReview === true
              );
            })
            .map(function (c) {
              return c.ID;
            });
          if (fromRows.length) markedIds = fromRows;
        }

        // Saved settings may ride along on the list response (e.g.
        // { Settings: {...} } or { RapidCcsSettings: {...} }). If present,
        // merge them so HideRapidDisclaimer can suppress the entry
        // disclaimer before it's ever shown.
        const savedSettings =
          (data && (data.Settings || data.RapidCcsSettings)) || null;

        this.setState(function (prevState) {
          const nextState = { cases: list, loading: false };
          if (markedIds) nextState.marked = markedIds;
          if (savedSettings) {
            // Same tolerant parsing as the login-provided initialSettings
            // prop (object or JSON string).
            nextState.settings = parseInitialSettings(
              savedSettings,
              this.props,
            );
            if (this.props.onSettingsChange) {
              this.props.onSettingsChange(nextState.settings);
            }
          } else {
            nextState.settings = prevState.settings;
          }
          return nextState;
        });
      })
      .catch((err) => {
        if (err && err.name === "AbortError") return;
        if (!this._mounted) return;
        if (err && err.status === 401) {
          this.setState({ loading: false });
          return;
        }
        this.setState({
          error: "Could not load cases. Check your connection and try again.",
          loading: false,
        });
      });
  };

  toggleMark = (caseId) => {
    const next = this.state.marked.indexOf(caseId) === -1;
    // optimistic update
    this.setState(function (prevState) {
      return {
        marked: next
          ? prevState.marked.concat([caseId])
          : prevState.marked.filter(function (x) {
              return x !== caseId;
            }),
      };
    });
    this.api
      .saveRapidMarkedCase(caseId, next)
      .then((res) => {
        if (res && Array.isArray(res.MarkedCases) && this._mounted) {
          this.setState({ marked: res.MarkedCases });
        }
      })
      .catch(() => {
        // revert on failure
        if (!this._mounted) return;
        this.setState(function (prevState) {
          return {
            marked: next
              ? prevState.marked.filter(function (x) {
                  return x !== caseId;
                })
              : prevState.marked.concat([caseId]),
          };
        });
      });
  };

  updateSettings = (settings) => {
    this.applySettings(settings);
  };

  // ── Settings modal (opened from either the selector or the case screen) ───
  openSettings = () => {
    this.setState({ showSettings: true });
  };
  closeSettings = () => {
    this.setState({ showSettings: false });
  };
  handleSettingsSaved = (applied) => {
    this.updateSettings(applied);
    this.setState({ showSettings: false });
  };

  // ── Entry disclaimer ──────────────────────────────────────────────────────

  // Continue is only clickable once the acknowledgement checkbox is checked,
  // and that checkbox doubles as the "don't show again" opt-out — so
  // continuing always flips HideRapidDisclaimer and persists the full
  // settings object to the backend. It can be re-enabled later from the
  // SettingsModal.
  dismissDisclaimer = () => {
    const nextSettings = Object.assign({}, this.state.settings, {
      HideRapidDisclaimer: true,
    });

    this.applySettings(nextSettings, { disclaimerDismissed: true });
    this.props.DismissRapidCCSDisclaimer();
  };

  // Cancel on the disclaimer = the user doesn't want Rapid CCS at all; hand
  // control back to the host so it can navigate away.
  cancelDisclaimer = () => {
    if (this.props.onExit) this.props.onExit();
  };

  // ── Screen switching ──────────────────────────────────────────────────────

  // Clicking a case: if it has previous attempts, offer the choice between a
  // new attempt and reviewing an old grade; otherwise start straight away.
  openCase = (caseRow) => {
    if (Array.isArray(caseRow.Attempts) && caseRow.Attempts.length > 0) {
      this.openAttemptsModal(caseRow);
    } else {
      this.startCase(caseRow);
    }
  };

  startCase = (caseRow) => {
    this.setState(function (prevState) {
      return {
        activeCase: caseRow,
        screen: "case",
        caseAttempt: prevState.caseAttempt + 1,
        grade: null,
        gradedDiagnosis: null,
        attemptsCase: null,
        attemptLoadingId: null,
        attemptError: null,
      };
    });
    this.persistSettings();
  };

  // ── Previous attempts modal ───────────────────────────────────────────────

  openAttemptsModal = (caseRow) => {
    this.setState({
      attemptsCase: caseRow,
      attemptLoadingId: null,
      attemptError: null,
    });
  };

  closeAttemptsModal = () => {
    if (this._attemptController) this._attemptController.abort();
    this.setState({
      attemptsCase: null,
      attemptLoadingId: null,
      attemptError: null,
    });
  };

  // "View grade" on a previous attempt: fetch its stored grade from
  // rapidccsgrading.webapi and show it on the normal grade screen.
  viewAttemptGrade = (attempt) => {
    const caseRow = this.state.attemptsCase;
    if (!caseRow) return;

    if (this._attemptController) this._attemptController.abort();
    this._attemptController = new AbortController();

    this.setState({ attemptLoadingId: attempt.ID, attemptError: null });

    this.api
      .getGradeForAttempt(
        caseRow.ID,
        attempt.ID,
        this._attemptController.signal,
      )
      .then((grade) => {
        if (!this._mounted) return;
        if (!grade) {
          this.setState({
            attemptLoadingId: null,
            attemptError: "No grade was returned for that attempt.",
          });
          return;
        }
        this.setState({
          activeCase: caseRow,
          screen: "grade",
          grade: grade,
          gradedDiagnosis: caseRow.DIAGNOSIS || null,
          attemptsCase: null,
          attemptLoadingId: null,
          attemptError: null,
        });
        this.persistSettings();
      })
      .catch((err) => {
        if (err && err.name === "AbortError") return;
        if (!this._mounted) return;
        this.setState({
          attemptLoadingId: null,
          attemptError:
            "Could not load that attempt's grade. Check your connection and try again.",
        });
      });
  };

  // Switching the app-bar toggle updates the screen immediately. The choice
  // is stored as ClassicGradingPage on RapidCcsSettings but only actually
  // saved to the server at the next persistSettings call site (e.g. going
  // back to the case list), not on every toggle click.
  setGradeView = (view) => {
    const nextSettings = Object.assign({}, this.state.settings, {
      ClassicGradingPage: view === "classic",
    });
    this.applySettings(nextSettings, { gradeView: view });
  };

  handleGraded = (grade, diagnosis) => {
    this.setState({
      screen: "grade",
      grade: grade,
      gradedDiagnosis: diagnosis,
    });

    // Award gamification credit for completing this case, same as classic
    // mode's ReviewOrders.fetchGrading: pass along the graded case's id and
    // its TotalGrade percentage. Rapid's grade result (from gradeRapidCcs /
    // getGradeForAttempt) doesn't carry its own case id, so fall back to the
    // case that was active when grading kicked off.
    if (
      grade &&
      this.props.triggerGamificationEvent &&
      this.props.gamificationEnabled
    ) {
      console.log("Triggering a gamification event");
      const caseId =
        grade.caseId ??
        grade.CaseId ??
        (this.state.activeCase && this.state.activeCase.ID);
      this.props.triggerGamificationEvent([caseId], grade.TotalGrade);
    }
  };

  goBack = () => {
    this.persistSettings();
    this.props.onExit();
  };

  // Re-enter the same case fresh. The key on RapidCaseScreen includes
  // caseAttempt, so bumping it remounts the screen and startCaseRapid runs
  // again from scratch.
  retryCase = () => {
    this.setState(function (prevState) {
      return {
        screen: "case",
        caseAttempt: prevState.caseAttempt + 1,
        grade: null,
        gradedDiagnosis: null,
      };
    });
    this.persistSettings();
  };

  backToSelector = () => {
    this.setState({
      activeCase: null,
      screen: "selector",
      grade: null,
      gradedDiagnosis: null,
    });
    // Refresh the list so COMPLETED / AvgGradeForCase reflect the attempt
    // that was just graded.
    this.fetchCases();
    this.persistSettings();
  };

  // ── App bar title ─────────────────────────────────────────────────────────

  // Human-readable label for the current screen, shown in the app bar.
  // On the case screen, prefer the case's name if the row carries one
  // (field name varies by backend payload), falling back to its ID.
  pageTitle = () => {
    const { screen, activeCase } = this.state;

    if (screen === "case" && activeCase) {
      const name =
        activeCase.NAME ||
        activeCase.Name ||
        activeCase.TITLE ||
        activeCase.Title ||
        activeCase.CaseName ||
        null;
      if (name) return String(name);
      if (activeCase.ID !== undefined) return "Case #" + activeCase.ID;
      return "Case";
    }

    if (screen === "grade") return "Grade Report";

    return "Case Selection";
  };

  // ── Copy/cut/paste blocking (exam integrity) ──────────────────────────────
  // Only active on the case screen — blocking on the selector would break
  // paste into its search box, and the grade screen has nothing to protect.

  blockClipboard = (e) => {
    if (this.state.screen === "case") {
      e.preventDefault();
    }
  };

  // ─────────────────────────────────────────────────────────────────────────

  render() {
    const { screen, activeCase, caseAttempt, grade, gradedDiagnosis } =
      this.state;

    // First-entry disclaimer. Waits for the initial load so a saved
    // HideRapidDisclaimer setting (delivered with the case list) can
    // suppress it without a flash.
    const showDisclaimer =
      !this.state.loading &&
      !this.state.disclaimerDismissed &&
      !this.state.settings.HideRapidDisclaimer;

    const rootStyle = {
      // Prefer the live visual-viewport height (tracked in
      // componentDidMount) over a static 100%, so this root actually
      // shrinks when the on-screen keyboard opens instead of staying the
      // pre-keyboard size — see the componentDidMount comment for why that
      // matters (keyboard hiding the order-search box on tablets).
      height: this.state.viewportHeight
        ? this.state.viewportHeight + "px"
        : "100%",
      display: "flex",
      flexDirection: "column",
      minHeight: 0,
      // The stylesheet sizes text off this variable. Fixed at 1 until the
      // font-scale control returns (see TODO in state).
      "--rccs-font-scale": "1",
    };

    let body;
    if (screen === "case" && activeCase) {
      body = (
        <RapidCaseScreen
          key={activeCase.ID + "-" + caseAttempt}
          caseRow={activeCase}
          settings={this.state.settings}
          onUpdateSettings={this.updateSettings}
          onOpenSettings={this.openSettings}
          onExit={this.backToSelector}
          onGraded={this.handleGraded}
          api={this.api} // every endpoint + createOrderSearch + exam metadata
        />
      );
    } else if (screen === "grade" && grade) {
      body =
        this.state.gradeView === "classic" ? (
          <RapidNewGrading
            grade={grade}
            diagnosis={gradedDiagnosis}
            caseRow={activeCase}
            onBackToCases={this.backToSelector}
            onRetry={this.retryCase}
            reviewLater={
              !!activeCase && this.state.marked.indexOf(activeCase.ID) !== -1
            }
            onToggleReviewLater={
              activeCase ? () => this.toggleMark(activeCase.ID) : undefined
            }
            PreventiveCareGrading={this.state.settings.PreventiveCareGrading}
            onSubmitFeedback={(feedbackText) =>
              this.api.submitFeedback(
                feedbackText,
                activeCase ? activeCase.ID : undefined,
                gradedDiagnosis,
              )
            }
          />
        ) : (
          <RapidGradeScreen
            grade={grade}
            diagnosis={gradedDiagnosis}
            onBackToCases={this.backToSelector}
            onRetry={this.retryCase}
            PreventiveCareGrading={this.state.settings.PreventiveCareGrading}
          />
        );
    } else {
      body = (
        <RapidCaseSelector
          cases={this.state.cases}
          marked={this.state.marked}
          loading={this.state.loading}
          error={this.state.error}
          onSelectCase={this.openCase}
          onToggleMark={this.toggleMark}
          onRetry={this.fetchCases}
          onReviewGrade={this.openAttemptsModal}
          onOpenSettings={this.openSettings}
          showDiagnosis={this.state.settings.ShowDiagnosis}
          hideCaseTitle={this.state.settings.HideCaseTitle}
          hideCategory={this.state.settings.HideCategory}
          hideCompleted={this.state.settings.HideCompleted}
          hideIncomplete={this.state.settings.HideIncomplete}
          reviewLaterOnly={this.state.settings.ReviewLaterOnly}
          onFilterChange={this.setCaseListFilter}
          initialScrollTop={this.caseListScrollTop}
          onScrollChange={this.saveCaseListScroll}
        />
      );
    }

    return (
      <GamificationToastWrapper messages={this.props.gamificationMessages}>
        <div
          style={rootStyle}
          className="rccs-root"
          onCopy={this.blockClipboard}
          onCut={this.blockClipboard}
          onPaste={this.blockClipboard}
        >
          <header className="rccs rccs-appbar">
            <div className="rccs-appbar__titles">
              <span className="rccs-appbar__brand">Rapid CCS</span>
              <span className="rccs-appbar__page" title={this.pageTitle()}>
                {this.pageTitle()}
              </span>
            </div>
            {screen === "grade" && grade && (
              <div
                className="rccs-appbar__seg"
                role="group"
                aria-label="Grade view"
              >
                <button
                  type="button"
                  className={
                    "rccs-appbar__seg-btn" +
                    (this.state.gradeView !== "classic"
                      ? " rccs-appbar__seg-btn--active"
                      : "")
                  }
                  aria-pressed={this.state.gradeView !== "classic"}
                  onClick={() => this.setGradeView("rapid")}
                >
                  Rapid
                </button>
                <button
                  type="button"
                  className={
                    "rccs-appbar__seg-btn" +
                    (this.state.gradeView === "classic"
                      ? " rccs-appbar__seg-btn--active"
                      : "")
                  }
                  aria-pressed={this.state.gradeView === "classic"}
                  onClick={() => this.setGradeView("classic")}
                >
                  Classic
                </button>
              </div>
            )}
            <div className="rcss-header__button_block">
              <button
                className="rccs-header__back"
                onClick={this.backToSelector}
                aria-label="Rapid Cases List"
              >
                <FontAwesomeIcon icon={faList} />
                <span className="rccs-btn__label">Rapid Cases List</span>
              </button>
              <button
                className="rccs-appbar__close"
                onClick={this.goBack}
                aria-label={
                  screen === "selector"
                    ? "Exit Rapid CCS"
                    : "Close and return to case list"
                }
              >
                <FontAwesomeIcon icon={faArrowRight} />
                <span className="rccs-btn__label">Back To CCS Cases</span>
              </button>
            </div>
          </header>
          {body}
          {this.state.attemptsCase && (
            <div className="rccs">
              <PreviousAttemptsModal
                caseRow={this.state.attemptsCase}
                onClose={this.closeAttemptsModal}
                onStartNew={() => this.startCase(this.state.attemptsCase)}
                onViewAttempt={this.viewAttemptGrade}
                loadingAttemptId={this.state.attemptLoadingId}
                error={this.state.attemptError}
                titleHidden={this.state.settings.HideCaseTitle}
              />
            </div>
          )}
          {showDisclaimer && (
            <RapidCcsDisclaimerModal
              onContinue={this.dismissDisclaimer}
              onCancel={this.cancelDisclaimer}
            />
          )}
          {this.state.showSettings && (
            <div className="rccs">
              <SettingsModal
                api={this.api}
                settings={this.state.settings}
                onClose={this.closeSettings}
                onSaved={this.handleSettingsSaved}
              />
            </div>
          )}
        </div>
      </GamificationToastWrapper>
    );
  }
}

export default RapidCcsApp;
